import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowRightOutlined, StarFilled } from '@ant-design/icons';
import { TableColumn } from 'react-data-table-component';
import moment from 'jalali-moment';
import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { CopyablePhone } from 'components';
import { Table } from 'components/table/table';
import { Badge } from 'components';
import { Button, buttonType } from 'components/button/button';
import { PaginationList, UserScoreLog } from 'core/domain';
import { getUserScoreLogs } from 'core/repositories/user-score';
import { scoreTypeBadge } from '../score-type';
import '../user-score-logs.scss';

const PAGE_SIZE = 20;

export const UserScoreDetail: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const state = (location.state || {}) as { fullName?: string; phoneNumber?: string };

    const [data, setData] = React.useState<PaginationList<UserScoreLog>>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [pageSize, setPageSize] = React.useState<number>(PAGE_SIZE);

    React.useEffect(() => {
        if (userId) {
            getUserScoreLogs(setLoading, setData, 1, PAGE_SIZE, userId);
        }
    }, [userId]);

    const columns: TableColumn<UserScoreLog>[] = [
        {
            name: 'امتیاز',
            id: 'score',
            selector: (row) => row.score,
            width: '120px',
            cell: (row) => (
                <span className="score-cell">
                    <StarFilled className="score-star" />
                    {row.score ?? '—'}
                </span>
            ),
        },
        {
            name: 'نوع امتیاز',
            id: 'scoreType',
            selector: (row) => row.scoreType?.title || '',
            width: '180px',
            cell: (row) =>
                row.scoreType ? (
                    <Badge text={row.scoreType.title} type={scoreTypeBadge(row.scoreType.id)} />
                ) : (
                    <span className="empty-val">—</span>
                ),
        },
        {
            name: 'زیرمجموعه',
            id: 'referral',
            width: '120px',
            cell: (row) =>
                row.referralUserId ? <Badge text="دارد" type="success" /> : <span className="empty-val">—</span>,
        },
        {
            name: 'تاریخ',
            id: 'createdOn',
            selector: (row) => row.createdOn,
            width: '180px',
            cell: (row) => (
                <span className="date-cell">
                    {row.createdOn
                        ? moment.utc(row.createdOn).locale('fa').local().format('YYYY/MM/DD HH:mm')
                        : '—'}
                </span>
            ),
        },
    ];

    return (
        <Page className="pmx-user-score-logs" title="تاریخچه امتیازهای کاربر">
            <Card title="تاریخچه امتیازهای کاربر" blur={loading}>
                <div className="filter-row detail-header">
                    <Button
                        text="بازگشت"
                        type={buttonType.outlined}
                        icon={<ArrowRightOutlined />}
                        onClick={() => navigate(-1)}
                    />
                    {(state.fullName || state.phoneNumber) && (
                        <div className="user-info">
                            {state.fullName && <span className="user-name">{state.fullName}</span>}
                            {state.phoneNumber && <CopyablePhone className="user-phone" phoneNumber={state.phoneNumber} />}
                        </div>
                    )}
                </div>

                <Table
                    blur={loading}
                    className="score-logs-table"
                    data={data?.items ?? []}
                    column={columns}
                    pagination={data?.pagination}
                    getdata={getUserScoreLogs}
                    setloader={setLoading}
                    setdata={setData}
                    filters={userId}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                />
            </Card>
        </Page>
    );
};
